import React, { useState } from "react";
import axios from "axios";
import "./ClientSetupForm.css";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { formatName } from "../utils/formatName"; // adjust path as needed
import { isGibberish } from "../utils/isGibberish";
import { BACKEND_BASE_URL } from "../config/appUrls";
import AlertModal from "./AlertModal";

const language = i18n.language || "en";

const ClientSetupForm = props => {
  const isStandalone = props.mode === "standalone" ? true : false;
  // If embedded, we expect some data to come from props.steps and some user edits.
  // If standalone in pricing flow, we expect no pre-filled data and user fills everything.

  const steps = props.steps || {};
  const onComplete = props.onComplete;
  const { t } = useTranslation();
  const rawName = steps?.name?.value || "";
  const name = formatName(rawName, {
    locale: language,
    offensiveWords: t("offensiveNames", { returnObjects: true }),
    fallbackName: t("pacobot.fallbackName")
  });
  const [alertMsg, setAlertMsg] = useState("");

  const LOCKED_FIELDS = isStandalone
    ? new Set([]) // nothing locked
    : new Set([
        "ownerName",
        "businessType",
        "businessDesc",
        "clientReq",
        "nextAction"
      ]);

  let goal = steps.goalInput?.value.trim();
  if (!goal || isGibberish(goal, { locale: i18n.language || "en" })) {
    goal = t("pacobot.defaultGoal").toLowerCase();
  }

  const [formData, setFormData] = useState({
    // All fieldsEMPTY in standalone, prefilled otherwise
    ownerName: isStandalone
      ? ""
      : name,

    businessType: isStandalone
      ? ""
      : steps.gptTypeChecker?.value ||
        t("pacobot.fallbackBusiness").toLowerCase(),

    businessDesc: isStandalone
      ? ""
      : steps.manualTypeEntry?.value || steps.businessDesc?.value || "",

    clientReq: isStandalone ? "" : goal,

    nextAction: isStandalone
      ? ""
      : steps.engagementOptions?.value || "",

    phone: "",
    email: "",
    city: "",

    commentFeedback: isStandalone ? "" : "",

    // ✅ default TRUE in standalone (no checkbox)
    contactOk: isStandalone ? true : false
  });


  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [contactError, setContactError] = useState("");

  const hasContact = () =>
    (formData.email || "").trim().length > 0 ||
    (formData.phone || "").trim().length > 0;

  const handleChange = e => {
    const { name, value, type, checked } = e.target;

    // Prevent editing locked fields (even if browser/autofill tries)
    if (LOCKED_FIELDS.has(name)) return;

    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
    if (name === "email" || name === "phone") {
      setContactError("");
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setContactError("");
    if (!hasContact()) {
      setContactError(
        t("pacobot.needEmailOrPhone") || "Please enter an email or phone."
      );
      return;
    }

    try {
      setIsSubmitted(true);
      const response = await axios.post(`${BACKEND_BASE_URL}/api/clients`, {
        ...formData,
        languageCode: language
      });
      const clientId = response.data.clientId;

      console.log("✅ Client’s business form saved successfully");
      setFormDisabled(true);
      onComplete?.({ status: "success", client_id: clientId });
      if (!isStandalone) props.triggerNextStep({ trigger: "registration" });
    } catch (err) {
      console.error("❌ Error: saving client's data", err);
      setAlertMsg(t("clientForm.saveError", "There was an error saving the data."));
      setIsSubmitted(false);
    }
  };

  return (
    <form className="client-form" onSubmit={handleSubmit}>
      <h2>{t("clientForm.title")}</h2>
      <input
        type="text"
        name="ownerName"
        value={formData.ownerName}
        onChange={handleChange}
        readOnly={!isStandalone}
        placeholder={t("clientForm.ownerName")}
        className="w-full border border-gray-300 rounded-lg p-3"
        maxLength={50}
        disabled={formDisabled}
        onInvalid={e => e.target.setCustomValidity(t("pacobot.pleaseFill"))}
        onInput={e => e.target.setCustomValidity("")}
        required
      />
      <input
        type="text"
        name="businessName"
        value={formData.businessName}
        onChange={handleChange}
        placeholder={t("clientForm.businessName")}
        className="w-full border border-gray-300 rounded-lg p-3"
        maxLength={50}
        disabled={formDisabled}
        onInvalid={e => e.target.setCustomValidity(t("pacobot.pleaseFill"))}
        onInput={e => e.target.setCustomValidity("")}
        required
      />
      <input
        type="text"
        name="businessType"
        value={formData.businessType}
        onChange={handleChange}
        readOnly={!isStandalone}
        placeholder={t("clientForm.businessType")}
        className="w-full border border-gray-300 rounded-lg p-3"
        maxLength={30}
        disabled={formDisabled}
        onInvalid={e => e.target.setCustomValidity(t("pacobot.pleaseFill"))}
        onInput={e => e.target.setCustomValidity("")}
        required={!isStandalone}
      />
      <input
        type="text"
        name="businessDesc"
        value={formData.businessDesc}
        onChange={handleChange}
        readOnly={!isStandalone}
        placeholder={t("clientForm.businessDesc")}
        className="w-full border border-gray-300 rounded-lg p-3"
        maxLength={50}
        disabled={formDisabled}
        onInvalid={e => e.target.setCustomValidity(t("pacobot.pleaseFill"))}
        onInput={e => e.target.setCustomValidity("")}
        required
      />
      {!isStandalone && (
        <input
          type="text"
          name="clientReq"
          value={formData.clientReq}
          onChange={handleChange}
          readOnly={!isStandalone}
          placeholder={t("clientForm.clientReq")}
          className="w-full border border-gray-300 rounded-lg p-3"
          maxLength={200}
          disabled={formDisabled}
          onInvalid={e => e.target.setCustomValidity(t("pacobot.pleaseFill"))}
          onInput={e => e.target.setCustomValidity("")}
          required={!isStandalone}
        />
      )}
      {!isStandalone && (
        <input
          type="text"
          name="nextAction"
          value={formData.nextAction}
          onChange={handleChange}
          readOnly={!isStandalone}
          placeholder={t("clientForm.nextAction")}
          className="w-full border border-gray-300 rounded-lg p-3"
          maxLength={200}
          disabled={formDisabled}
          onInvalid={e => e.target.setCustomValidity(t("pacobot.pleaseFill"))}
          onInput={e => e.target.setCustomValidity("")}
          required={!isStandalone}
        />
      )}
      <input
        type="tel"
        name="phone"
        value={formData.phone}
        onChange={handleChange}
        placeholder={t("clientForm.phone")}
        className="w-full border border-gray-300 rounded-lg p-3"
        maxLength={15}
        disabled={formDisabled}
        onInvalid={e => e.target.setCustomValidity(t("pacobot.pleaseFill"))}
        onInput={e => e.target.setCustomValidity("")}
        // required
      />
      <input
        type="email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        placeholder={t("clientForm.email")}
        className="w-full border border-gray-300 rounded-lg p-3"
        maxLength={100}
        disabled={formDisabled}
        onInvalid={e => e.target.setCustomValidity(t("pacobot.pleaseFill"))}
        onInput={e => e.target.setCustomValidity("")}
        // required
      />
      {contactError && (
        <div style={{ color: "#b00020", marginTop: "8px", fontSize: "14px" }}>
          {contactError}
        </div>
      )}
      <input
        type="text"
        name="city"
        value={formData.city}
        onChange={handleChange}
        placeholder={t("clientForm.city")}
        className="w-full border border-gray-300 rounded-lg p-3"
        disabled={formDisabled}
        onInvalid={e => e.target.setCustomValidity(t("pacobot.pleaseFill"))}
        onInput={e => e.target.setCustomValidity("")}
        required
      />
      {!isStandalone && (
        <input
          type="text"
          name="commentFeedback"
          value={formData.commentFeedback}
          onChange={handleChange}
          placeholder={t("clientForm.commentFeedback")}
          className="w-full border border-gray-300 rounded-lg p-3"
          maxLength={200}
          disabled={formDisabled}
          onInvalid={e => e.target.setCustomValidity(t("pacobot.pleaseFill"))}
          onInput={e => e.target.setCustomValidity("")}
        />
      )}
      {!isStandalone && (
        <label>
          <input
            type="checkbox"
            name="contactOk"
            checked={formData.contactOk}
            onChange={handleChange}
            disabled={formDisabled}
            onInvalid={e => e.target.setCustomValidity(t("pacobot.pleaseCheck"))}
            onInput={e => e.target.setCustomValidity("")}
            required
          />
          {t("clientForm.contactOk")}
        </label>
      )}
      <button
        type={isStandalone && isSubmitted ? "button" : "submit"}
        disabled={isSubmitted && !formDisabled}
        onClick={() => {
          if (isStandalone && formDisabled) {
            props.onContinue?.();
          }
        }}
        className={`submit-button ${
          isSubmitted && !formDisabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {isStandalone && formDisabled
          ? t("pacobot.continueToSubscription")
          : isSubmitted
          ? t("pacobot.btnSubmitted")
          : t("pacobot.btnSaveAndContinue")}
      </button>{" "}
      <AlertModal
        open={Boolean(alertMsg)}
        message={alertMsg}
        onClose={() => setAlertMsg("")}
      />
    </form>
  );
};

export default ClientSetupForm;
